import { useState, useEffect, useMemo } from 'react';
// Import Firebase for web environment
import { database as firestore, collection, onSnapshot, query, orderBy } from '../Utils/firebaseConfig.js';
import { useAuth } from './AuthProvider.jsx';
import {
  assignSummaryLanes,
  applyParentSummaryLayoutToResources,
  buildParentDescendantMap,
  collectParentResourceIds,
  normalizeCalendarDate,
} from '../Components/Calendar/summaryLineLayout.js';
import { isRentmanReturnedEvent } from '../Utils/rentmanStatus.js';

// Neutral grey for the summary line of a returned Rentman event, so it matches
// the greyed-out ("Returned") styling used in the event renderer.
const RETURNED_SUMMARY_COLOR = '#9e9e9e';

// Group header rows that should NOT show aggregated summary lines.
// Crew (100) is the top-level group and its lines just duplicate the per-person
// rows beneath it, so we skip it.
const SUMMARY_EXCLUDED_PARENT_IDS = new Set(['100']);

/**
 * Fetches all resources from Firestore. Works in both Electron and web.
 * - Web: Uses Firestore onSnapshot (offline-capable)
 * - Electron: Falls back to IPC if renderer Firestore fails
 * Also updates a global id->name cache for quick lookups in UI (notifications).
 */
export function useFirestoreResources() {
  const [resources, setResources] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    let unsubscribe;
    const listenViaRenderer = () => {
      // Only proceed if user is authenticated
      if (!user) {
        console.log("[useFirestoreResources] No authenticated user, skipping Firebase connection");
        setResources([]);
        return () => {};
      }
      console.log("[useFirestoreResources] Fetching resources via Firebase (renderer)");
      const resourcesQuery = query(collection(firestore, "resources"), orderBy("order"));
      unsubscribe = onSnapshot(resourcesQuery, (snapshot) => {
        const flatResources = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setResources(flatResources);
        // Build a global id->name map for components that need quick lookups (e.g., notifications)
        try {
          const nameMap = {};
          flatResources.forEach(r => { if (r && r.id != null && r.name) nameMap[r.id] = r.name; });
          window.__resourceNameMap = nameMap;
        } catch (_) {}
        console.log(`[useFirestoreResources] Retrieved ${flatResources.length} resources via Firebase (renderer)`);
      }, (error) => {
        console.error("[useFirestoreResources] ❌ Renderer Firestore failed:", error);
        // Fallback to IPC if available
        if (window.electronAPI?.getAllFirestoreData) {
          void fetchViaIpc();
        }
      });
      return () => unsubscribe && unsubscribe();
    };

    const fetchViaIpc = async () => {
      try {
        console.log("[useFirestoreResources] Fetching resources via Electron IPC fallback");
        const result = await window.electronAPI.getAllFirestoreData("resources");
        if (result?.success) {
          setResources(result.data);
          try {
            const nameMap = {};
            (result.data || []).forEach(r => { if (r && r.id != null && r.name) nameMap[r.id] = r.name; });
            window.__resourceNameMap = nameMap;
          } catch (_) {}
          console.log(`[useFirestoreResources] Retrieved ${result.data.length} resources via IPC`);
        } else {
          console.error("[useFirestoreResources] ❌ IPC error:", result?.error);
          setResources([]);
        }
      } catch (error) {
        console.error("[useFirestoreResources] ❌ IPC fetch failed:", error);
        setResources([]);
      }
    };

    // Always try renderer Firestore first (works offline). It will fallback to IPC on error.
    const cleanup = listenViaRenderer();
    return () => {
      if (unsubscribe) unsubscribe();
      if (typeof cleanup === 'function') cleanup();
    };
  }, [user]);

  return resources;
}

/**
 * A master hook that orchestrates all data processing for the calendar.
 * It now fetches the complete resource list from Firestore and reconstructs
 * the required nested tree structure for the calendar.
 */
export default function useCalendarData(allEvents, currentDate) {
  // 1. Fetch the complete, flat list of ALL resources from Firestore.
  const flatResources = useFirestoreResources();
  // 2. Reconstruct the nested tree structure that Mobiscroll requires.
  const allResources = useMemo(() => {
    if (!flatResources.length) return [];
    
    const resourceMap = new Map();
    const finalNestedList = [];

    // First pass: put everything in a map and ensure a 'children' array exists.
    flatResources.forEach(res => {
      resourceMap.set(res.id, { ...res, children: res.children || [] });
    });

    // Second pass: iterate and link children to their parents within the map.
    resourceMap.forEach(res => {
      if (res.parentId && resourceMap.has(res.parentId)) {
        const parent = resourceMap.get(res.parentId);
        // Add the child to the parent, but only if it's not already there.
        if (parent && !parent.children.some(c => c.id === res.id)) {
          parent.children.push(res);
        }
      } else if (!res.parentId) {
        // If a resource has no parentId, it's a top-level item (like "Crew").
        finalNestedList.push(res);
      }
    });

    // Sort children under each parent by `order` (same as top-level).
    resourceMap.forEach((res) => {
      if (res.children && res.children.length > 1) {
        res.children.sort((a, b) => (a.order || 0) - (b.order || 0));
      }
    });
    
    // Sort the final top-level list by the 'order' field.
    return finalNestedList.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [flatResources]);

  // 3. Create a Set of all valid resource IDs for fast filtering.
  const validResourceIds = useMemo(() => {
    const ids = new Set();
    const extractIds = (resources) => {
      resources.forEach(res => {
        ids.add(res.id);
        if (res.children) {
          extractIds(res.children);
        }
      });
    };
    extractIds(allResources);
    return ids;
  }, [allResources]);

  // 4. Filter events to only include those with valid resource assignments.
  const filteredEvents = useMemo(() => {
    return (allEvents || [])
      .filter(event => {
        if (event.visibleInCalendar === false) return false;

        const resources = Array.isArray(event.resource) ? event.resource : [event.resource];
        return resources.every(resourceId => validResourceIds.has(resourceId));
      })
      .map(event => ({
        ...event,
        vacation:
          event.vacation === true ||
          event.vacation === 'true' ||
          event.vacation === 1,
        sick:
          event.sick === true ||
          event.sick === 'true' ||
          event.sick === 1,
        sickChild:
          event.sickChild === true ||
          event.sickChild === 'true' ||
          event.sickChild === 1,
      }));
  }, [allEvents, validResourceIds]);

  // 5. Synthetic summary lines on group header rows (one thin line per child event).
  const summaryEventData = useMemo(() => {
    const empty = { summaryEvents: [], parentSummaryRowHeights: new Map() };
    if (!allResources.length || !filteredEvents.length) return empty;

    const parentDescendants = buildParentDescendantMap(allResources);

    if (parentDescendants.size === 0) return empty;

    const linesByParent = new Map();
    const seen = new Set();

    filteredEvents.forEach((ev) => {
      const eventResourceIds = (Array.isArray(ev.resource) ? ev.resource : [ev.resource])
        .filter((rid) => rid != null)
        .map(String);

      parentDescendants.forEach((descendantIds, parentId) => {
        if (SUMMARY_EXCLUDED_PARENT_IDS.has(String(parentId))) return;
        if (!eventResourceIds.some((rid) => descendantIds.has(rid))) return;

        const dedupeKey = `${parentId}-${ev.id}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);

        const isRentman = !!ev.rentmanEvent;
        const isReturned = isRentman && isRentmanReturnedEvent(ev);
        const isAllDay =
          ev.allDay === true || ev.allDay === 'true' || ev.allDay === 1;
        const start = normalizeCalendarDate(isRentman ? ev.planStart : ev.start);
        const end = normalizeCalendarDate(isRentman ? ev.planEnd : ev.end);
        if (!start || !end || start.getTime() > end.getTime()) return;

        const summary = {
          id: `summary-${String(parentId)}-${String(ev.id)}`,
          resource: String(parentId),
          start,
          end,
          title: '',
          color: isReturned
            ? RETURNED_SUMMARY_COLOR
            : typeof ev.color === 'string'
              ? ev.color
              : undefined,
          allDay: isAllDay,
          isSummaryLine: true,
          editable: false,
          cssClass: 'summary-line-event',
        };

        if (isRentman) {
          const usageStart = normalizeCalendarDate(ev.usageStart);
          const usageEnd = normalizeCalendarDate(ev.usageEnd);
          if (usageStart && usageEnd) {
            summary.usageStart = usageStart;
            summary.usageEnd = usageEnd;
          }
        }

        if (!linesByParent.has(parentId)) {
          linesByParent.set(parentId, []);
        }
        linesByParent.get(parentId).push(summary);
      });
    });

    const parentSummaryRowHeights = new Map();
    const lines = [];

    linesByParent.forEach((parentLines, parentId) => {
      const { events: linedEvents, rowHeightPx } = assignSummaryLanes(parentLines);
      parentSummaryRowHeights.set(String(parentId), rowHeightPx);
      linedEvents.forEach((line) => {
        lines.push({
          ...line,
          cssClass: `summary-line-event summary-lane-${line.summaryLane}`,
        });
      });
    });

    return { summaryEvents: lines, parentSummaryRowHeights };
  }, [allResources, filteredEvents]);

  const summaryEvents = summaryEventData.summaryEvents;
  const parentSummaryRowHeights = summaryEventData.parentSummaryRowHeights;

  // Only parent (group header) rows need an explicit height: MobiScroll gives
  // them a fixed parentRowHeight and does not grow them for our stacked summary
  // lines. Leaf rows are sized natively by MobiScroll's rowHeight: 'variable'
  // (correct now that the global event height is measured properly) — overriding
  // them here risked clipping tall rows (e.g. an event overflowing its row).
  const rowHeightsById = useMemo(
    () => new Map(parentSummaryRowHeights),
    [parentSummaryRowHeights]
  );

  const resources = useMemo(() => {
    if (!parentSummaryRowHeights.size) {
      try {
        window.__parentResourceIds = new Set(
          collectParentResourceIds(allResources).map(String)
        );
      } catch (_) {}
      return allResources;
    }

    const withLayout = applyParentSummaryLayoutToResources(
      allResources,
      parentSummaryRowHeights
    );
    try {
      window.__parentResourceIds = new Set(
        collectParentResourceIds(withLayout).map(String)
      );
    } catch (_) {}
    return withLayout;
  }, [allResources, parentSummaryRowHeights]);

  // 6. Generate the dynamic background colors for the calendar.
  const calendarColors = useMemo(() => {
    const colors = [];
    if (!allResources.length) return [];

    const allResourceIds = Array.from(validResourceIds);

    colors.push({
      background: '#FFF3F4',
      recurring: { repeat: 'weekly', weekDays: 'SU' },
    });
    colors.push({
      background: '#f7f2f2',
      recurring: { repeat: 'weekly', weekDays: 'SA' },
    });
    colors.push({
      date: currentDate,
      background: 'rgb(227, 255, 227)',
      resource: allResourceIds,
    });
    
    return colors;
  }, [allResources, currentDate, validResourceIds]);

  // Return everything the calendar component needs.
  return {
    resources,
    filteredEvents,
    summaryEvents,
    parentSummaryRowHeights,
    rowHeightsById,
    calendarColors,
  };
}

